import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { adminButton, adminField, adminFocusable, adminTokens as a } from '../../admin-ui';

export interface TagFilterOption {
  id: number;
  name: string;
  /** Pre-resolved by the caller: lessonCount on the Lessons list, courseCount
   * on the Courses list — this component only renders whatever it's given
   * (the count-scoping decision itself belongs to the tags API, not here). */
  usageCount: number;
}

interface TagFilterPanelState {
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

function useTagFilterPanel(): TagFilterPanelState {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((value) => !value), []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      if (
        containerRef.current
        && event.target instanceof Node
        && !containerRef.current.contains(event.target)
      ) {
        close();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [close, isOpen]);

  return { isOpen, toggle, close, containerRef };
}

export interface TagFilterControlProps {
  options: TagFilterOption[];
  selectedTagIds: string[];
  onChange: (tagIds: string[]) => void;
}

/** The "Tags ▾" button + its filter panel — lives in the list toolbar, beside search. */
export function TagFilterControl({ options, selectedTagIds, onChange }: TagFilterControlProps) {
  const { isOpen, toggle, containerRef } = useTagFilterPanel();
  const [query, setQuery] = useState('');

  const selectedSet = useMemo(() => new Set(selectedTagIds), [selectedTagIds]);
  const filteredOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => option.name.toLowerCase().includes(needle));
  }, [options, query]);

  const toggleTag = (id: number) => {
    const idStr = String(id);
    onChange(selectedSet.has(idStr) ? selectedTagIds.filter((tagId) => tagId !== idStr) : [...selectedTagIds, idStr]);
  };

  return (
    <div ref={containerRef} style={styles.container}>
      <button
        type="button"
        onClick={toggle}
        style={{
          ...styles.trigger,
          ...(isOpen || selectedTagIds.length > 0 ? styles.triggerActive : {}),
        }}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        Tags
        <span aria-hidden="true" style={styles.caret}>▾</span>
      </button>

      {isOpen && (
        <div style={styles.panel} role="dialog" aria-label="Filter by tag">
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Find a tag..."
            style={styles.panelSearch}
            aria-label="Find a tag"
            autoFocus
          />
          <div style={styles.optionList}>
            {filteredOptions.length === 0 ? (
              <div style={styles.optionEmpty}>No tags match.</div>
            ) : (
              filteredOptions.map((option) => {
                const idStr = String(option.id);
                const checked = selectedSet.has(idStr);
                return (
                  <label key={option.id} style={styles.optionRow}>
                    <span style={styles.optionLeft}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleTag(option.id)}
                      />
                      {option.name}
                    </span>
                    <span style={styles.optionUsage}>{option.usageCount}</span>
                  </label>
                );
              })
            )}
          </div>
          <div style={styles.panelFooter}>
            <button type="button" onClick={() => onChange([])} style={styles.clearLink}>
              Clear
            </button>
            <span style={styles.selectedCount}>{selectedTagIds.length} selected</span>
          </div>
        </div>
      )}
    </div>
  );
}

export interface TagFilterChipRowProps {
  options: TagFilterOption[];
  selectedTagIds: string[];
  onChange: (tagIds: string[]) => void;
}

/** The removable-chip row + "Clear all" below the toolbar — visible whenever any tag is selected, panel open or not. */
export function TagFilterChipRow({ options, selectedTagIds, onChange }: TagFilterChipRowProps) {

  if (selectedTagIds.length === 0) return null;

  const nameById = new Map(options.map((option) => [String(option.id), option.name]));
  const removeTag = (id: string) => onChange(selectedTagIds.filter((tagId) => tagId !== id));

  return (
    <div style={styles.chipRow}>
      {selectedTagIds.map((id) => (
        <span key={id} style={styles.filterChip}>
          {nameById.get(id) ?? id}
          <button
            type="button"
            onClick={() => removeTag(id)}
            aria-label={`Remove ${nameById.get(id) ?? id} filter`}
            style={styles.chipRemove}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </span>
      ))}
      <button type="button" onClick={() => onChange([])} style={styles.clearAllLink}>
        Clear all
      </button>
    </div>
  );
}

export interface TagNameListProps {
  tags: { id: number; name: string }[];
}

/** Small pills for the list rows' conditional Tags column — matched tags only. */
export function TagNameList({ tags }: TagNameListProps) {
  if (tags.length === 0) return null;
  return (
    <span style={styles.chipRow}>
      {tags.map((tag) => (
        <span key={tag.id} style={styles.filterChip}>{tag.name}</span>
      ))}
    </span>
  );
}

/** Names the active tag filters for the zero-result empty state. */
export function describeActiveTagFilters(options: TagFilterOption[], selectedTagIds: string[]): string {
  const nameById = new Map(options.map((option) => [String(option.id), option.name]));
  return selectedTagIds.map((id) => nameById.get(id) ?? id).join(', ');
}

// Lists-only filter control (Lessons, Courses), so it uses the admin recipes.
const styles: Record<string, CSSProperties> = {
  container: {
    position: 'relative',
  },
  trigger: {
    ...adminButton.secondary,
    gap: '6px',
    height: '38px',
    padding: '0 14px',
    fontWeight: 400,
  },
  triggerActive: {
    border: `1px solid ${a.text.primary}`,
    fontWeight: 600,
  },
  caret: {
    fontSize: '10px',
    color: a.text.secondary,
  },
  panel: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    width: '320px',
    maxWidth: 'calc(100vw - 32px)',
    border: `1px solid ${a.border.subtle}`,
    borderRadius: a.radius.tile,
    backgroundColor: a.surface.panel,
    boxShadow: a.shadow.overlay,
    zIndex: 30,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  panelSearch: {
    ...adminField.input,
    margin: a.spacing.sm,
    height: '34px',
    padding: '0 10px',
  },
  optionList: {
    maxHeight: '260px',
    overflowY: 'auto',
    padding: a.spacing.xs,
    borderTop: `1px solid ${a.border.subtle}`,
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${a.spacing.xs} ${a.spacing.sm}`,
    borderRadius: a.radius.control,
    fontSize: a.typography.body.size,
    color: a.text.primary,
    cursor: 'pointer',
  },
  optionLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: a.spacing.sm,
  },
  optionUsage: {
    fontSize: a.typography.label.size,
    color: a.text.secondary,
  },
  optionEmpty: {
    padding: a.spacing.sm,
    fontSize: a.typography.label.size,
    color: a.text.secondary,
  },
  panelFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${a.spacing.sm} ${a.spacing.md}`,
    borderTop: `1px solid ${a.border.subtle}`,
  },
  clearLink: adminButton.link,
  selectedCount: {
    fontSize: a.typography.label.size,
    color: a.text.secondary,
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: a.spacing.xs,
  },
  filterChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: a.radius.pill,
    backgroundColor: a.surface.subtle,
    color: a.text.primary,
    fontSize: a.typography.label.size,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  chipRemove: {
    ...adminFocusable,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
    color: 'inherit',
    cursor: 'pointer',
    padding: 0,
    fontSize: '11px',
  },
  clearAllLink: adminButton.link,
};
